import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  computed,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import Chart from 'chart.js/auto';
import { Subscription, forkJoin, of } from 'rxjs';
import { catchError, map, skip } from 'rxjs/operators';
import { TranslocoService } from '@ngneat/transloco';

import {
  CompanyControllerService,
  LocationControllerService,
  LoyaltyProgramControllerService,
} from 'src/app/api-client';
import {
  ConsoleDashboardDto,
  ConsoleDashboardV2Service,
} from 'src/app/core/api-v2/console-dashboard-v2.service';
import {
  CompanyVerificationDto,
  ConsoleVerificationV2Service,
} from 'src/app/core/api-v2/console-verification-v2.service';
import { SharedModule } from 'src/app/shared/shared.module';
import { NotificationService } from 'src/app/shared/services/notification.service';
import { TokenService } from 'src/app/shared/services/token.service';
import { TranslocoRootModule } from 'src/app/transloco-root.module';

/** View model for one KPI tile in the stats row. */
interface StatCard {
  labelKey: string;
  labelParams: Record<string, unknown>;
  value: string;
  /** When set, the tile renders this points value via <app-lomy-points>. */
  pointsValue?: number | null;
  delta: { up: boolean; text: string; neutral?: boolean } | null;
}

/**
 * How many activity rows the dashboard card renders. The endpoint returns up to
 * 20; the card is a glance at what just happened, not the history, so it shows
 * only the newest few and links to /transactions for the rest.
 */
const ACTIVITY_FEED_MAX_ROWS = 5;

/** View model for one entry in the recent-activity feed. */
interface FeedItem {
  icon: string;
  tone: 'success' | 'warn' | 'accent' | 'info';
  title: string;
  sub: string;
  time: string;
}

type OnboardingStep =
  | 'program'
  | 'personalization'
  | 'location'
  | 'coupon'
  | 'verification'
  | 'fullVersion';

/** One usage tip shown in the dashboard hints carousel. */
interface DashboardHint {
  icon: string;
  titleKey: string;
  textKey: string;
  ctaKey: string;
  /** In-app path opened by the CTA, an absolute URL, or 'help' for the user manual. */
  action: string;
}

const ONBOARDING_STEP_COUNT = 6;
const ACCENT = '#fe3eaa';
const HINTS_HIDDEN_KEY = 'LOMY_DASHBOARD_HINTS_HIDDEN';
const HINT_ROTATE_MS = 12000;

/**
 * Console v2 home dashboard: greeting, verification banner,
 * getting-started progress, KPI tiles, points-over-time chart
 * and the recent-activity feed.
 */
@Component({
  selector: 'app-company-statistics',
  standalone: true,
  imports: [CommonModule, RouterModule, SharedModule, TranslocoRootModule],
  templateUrl: './company-statistics.component.html',
  styleUrls: ['./company-statistics.component.scss'],
})
export class CompanyStatisticsComponent implements OnInit, OnDestroy {
  @ViewChild('pointsChart')
  private chartCanvas?: ElementRef<HTMLCanvasElement>;

  private _dashboardApi = inject(ConsoleDashboardV2Service);
  private _verificationApi = inject(ConsoleVerificationV2Service);
  private _company = inject(CompanyControllerService);
  private _loyaltyPrograms = inject(LoyaltyProgramControllerService);
  private _locations = inject(LocationControllerService);
  private _token = inject(TokenService);
  private _notification = inject(NotificationService);
  private _router = inject(Router);

  public _transloco = inject(TranslocoService);

  private translationsSub?: Subscription;
  private chart?: Chart<'line', number[], string>;

  /** Total number of getting-started steps (drives the progress "x of N"). */
  public readonly totalSteps = ONBOARDING_STEP_COUNT;

  /** Selected chart/statistics period in days. */
  public readonly dayOptions: number[] = [7, 14, 30];
  public days = signal<number>(7);

  public loading = signal<boolean>(false);
  public dashboard = signal<ConsoleDashboardDto | null>(null);
  public dashboardError = signal<boolean>(false);

  /** Bumped on language change so computed views recompute translated labels. */
  private langVersion = signal<number>(0);

  public verification = signal<CompanyVerificationDto | null>(null);
  public uploadingProof = signal<boolean>(false);

  /** Getting-started step completion flags. */
  public hasProgram = signal<boolean>(false);
  public hasPersonalization = signal<boolean>(false);
  public hasLocation = signal<boolean>(false);
  public hasCoupon = signal<boolean>(false);
  /**
   * True once the account is on the full (paid) version. On the trial this is
   * false, which surfaces the final "Puna verzija" getting-started step.
   */
  public hasFullVersion = signal<boolean>(false);
  /** True when a full-version upgrade request was submitted and awaits approval. */
  public onWaitingList = signal<boolean>(false);
  public stepsLoaded = signal<boolean>(false);
  public verificationLoaded = signal<boolean>(false);

  /** Usage tips shown one at a time in the dashboard hints carousel. */
  public readonly hints: DashboardHint[] = [
    { icon: 'receipt_long', titleKey: 'stats.hints.tryCustomer.title', textKey: 'stats.hints.tryCustomer.text', ctaKey: 'stats.hints.tryCustomer.cta', action: 'https://info.lomy.app/app' },
    { icon: 'groups', titleKey: 'stats.hints.members.title', textKey: 'stats.hints.members.text', ctaKey: 'stats.hints.members.cta', action: '/customer-loyalty' },
    { icon: 'notifications', titleKey: 'stats.hints.push.title', textKey: 'stats.hints.push.text', ctaKey: 'stats.hints.push.cta', action: '/notifications' },
    { icon: 'mail', titleKey: 'stats.hints.news.title', textKey: 'stats.hints.news.text', ctaKey: 'stats.hints.news.cta', action: '/announcements' },
    { icon: 'campaign', titleKey: 'stats.hints.promotion.title', textKey: 'stats.hints.promotion.text', ctaKey: 'stats.hints.promotion.cta', action: '/promotions' },
    { icon: 'local_offer', titleKey: 'stats.hints.validate.title', textKey: 'stats.hints.validate.text', ctaKey: 'stats.hints.validate.cta', action: 'help' },
    { icon: 'place', titleKey: 'stats.hints.locations.title', textKey: 'stats.hints.locations.text', ctaKey: 'stats.hints.locations.cta', action: '/locations' },
  ];
  public hintIndex = signal<number>(0);
  public hintsHidden = signal<boolean>(this.readHintsHidden());
  public activeHint = computed(() => this.hints[this.hintIndex()]);
  private hintTimer?: ReturnType<typeof setInterval>;

  /** Id of the first fetched loyalty program, used to open its personalization screen. */
  private firstProgramId = signal<number | null>(null);

  public firstName: string = this._token.getUser().firstName || '';

  /** Time-of-day aware greeting translation key. */
  public greetingKey: string = this.resolveGreetingKey();

  public completedCount = computed(() => {
    let count = 0;
    if (this.hasProgram()) count++;
    if (this.hasPersonalization()) count++;
    if (this.hasLocation()) count++;
    if (this.hasCoupon()) count++;
    if (this.verificationDone()) count++;
    // A submitted (pending) upgrade counts the final step as done — the user's
    // part is complete; only approval remains.
    if (this.hasFullVersion() || this.upgradePending()) count++;
    return count;
  });

  public progressPct = computed(
    () => (this.completedCount() / ONBOARDING_STEP_COUNT) * 100
  );

  /**
   * True while the full-version upgrade request is submitted but not yet
   * approved. The getting-started card then shows 6/6 and a "waiting for
   * approval" message instead of the upgrade CTA.
   */
  public upgradePending = computed(
    () => this.onWaitingList() && !this.hasFullVersion()
  );

  public showStartBanner = computed(
    () =>
      this.stepsLoaded() &&
      this.verificationLoaded() &&
      (this.completedCount() < ONBOARDING_STEP_COUNT || this.upgradePending())
  );

  public isVerified = computed(
    () => this.verification()?.status === 'VERIFIED'
  );

  /**
   * The verification onboarding step is the user's responsibility only up to
   * submitting the proof. Once the document is uploaded (and not rejected) the
   * step counts as handled for onboarding progress — the remaining admin review
   * is out of the user's hands. A REJECTED status re-opens the step so a new
   * document can be attached.
   */
  public verificationDone = computed(() => {
    const v = this.verification();
    return this.isVerified() || (!!v?.proofUploaded && v?.status !== 'REJECTED');
  });

  public statCards = computed<StatCard[]>(() => {
    this.langVersion(); // recompute translated labels on language change
    const d = this.dashboard();
    if (!d) return [];

    return [
      {
        labelKey: 'stats.revenueLabel',
        labelParams: { days: this.days() },
        value: `€${this.formatMoney(d.revenue)}`,
        delta: this.buildDelta(d.revenueDeltaPct),
      },
      {
        labelKey: 'stats.transactionsLabel',
        labelParams: {},
        value: this.formatInt(d.transactionCount),
        delta: this.buildDelta(d.transactionCountDeltaPct),
      },
      {
        labelKey: 'stats.pointsAwardedLabel',
        labelParams: {},
        value: this.formatInt(d.pointsAwarded),
        pointsValue: d.pointsAwarded,
        delta: this.buildDelta(d.pointsAwardedDeltaPct),
      },
      {
        labelKey: 'stats.couponsRedeemedLabel',
        labelParams: {},
        value: this.formatInt(d.couponsRedeemed),
        delta: this.buildDelta(d.couponsRedeemedDeltaPct),
      },
    ];
  });

  public feedItems = computed<FeedItem[]>(() => {
    this.langVersion(); // recompute translated labels on language change
    const d = this.dashboard();
    if (!d?.activity) return [];

    // The endpoint returns up to 20 events; the dashboard card only shows the
    // most recent few so it stays a glance rather than a list. "Prikazi sve"
    // in the card header goes to /transactions for the full history.
    return d.activity.slice(0, ACTIVITY_FEED_MAX_ROWS).map((entry) => {
      const time = this.formatTimeAgo(entry.timestamp);
      const email = entry.userEmail || '';

      switch (entry.type) {
        case 'TRANSACTION_APPROVED':
          return {
            icon: 'check',
            tone: 'success' as const,
            title: this._transloco.translate('stats.feedTransactionApproved', {
              points: this.formatPoints(entry.points ?? 0),
            }),
            sub: email,
            time,
          };
        case 'COUPON_REDEEMED':
          return {
            icon: 'local_offer',
            tone: 'accent' as const,
            title: this._transloco.translate('stats.feedCouponRedeemed', {
              name: entry.couponName || '',
            }),
            sub: email,
            time,
          };
        case 'TRANSACTION_PENDING':
          return {
            icon: 'schedule',
            tone: 'warn' as const,
            title: this._transloco.translate('stats.feedTransactionPending'),
            sub: this.buildPendingSub(entry.amount, entry.points),
            time,
          };
        case 'MEMBER_JOINED':
        default:
          return {
            icon: 'person_add',
            tone: 'info' as const,
            title: this._transloco.translate('stats.feedMemberJoined', {
              name: entry.loyaltyProgramName || '',
            }),
            sub: email,
            time,
          };
      }
    });
  });

  ngOnInit(): void {
    // Fetch data once on init; the period switch re-fetches via setDays().
    this.fetchDashboard();
    this.fetchVerification();
    this.fetchOnboardingSteps();

    // On a later language change only recompute translated labels + chart —
    // do not re-fetch. skip(1) drops the initial (current-language) emission.
    this.translationsSub = this._transloco.langChanges$
      .pipe(skip(1))
      .subscribe(() => {
        this.langVersion.update((v) => v + 1);
        this.renderChart();
      });

    this.startHintTimer();
  }

  ngOnDestroy(): void {
    this.translationsSub?.unsubscribe();
    this.destroyChart();
    this.stopHintTimer();
  }

  // -----------------------------------------------------------------
  // Usage-tips carousel
  // -----------------------------------------------------------------

  /** Advance to the next tip (wraps) and restart the auto-rotate timer. */
  public nextHint(): void {
    this.hintIndex.update((i) => (i + 1) % this.hints.length);
    this.startHintTimer();
  }

  /** Go to the previous tip (wraps) and restart the auto-rotate timer. */
  public prevHint(): void {
    this.hintIndex.update((i) => (i - 1 + this.hints.length) % this.hints.length);
    this.startHintTimer();
  }

  /** Dismiss the carousel; the choice is remembered across sessions. */
  public closeHints(): void {
    this.stopHintTimer();
    this.hintsHidden.set(true);
    try {
      localStorage.setItem(HINTS_HIDDEN_KEY, 'true');
    } catch {
      /* localStorage unavailable — dismissal is session-only */
    }
  }

  /** CTA action for the active tip: open its screen or the user manual. */
  public openHintAction(): void {
    const action = this.activeHint().action;
    if (action.startsWith('http')) {
      window.open(action, '_blank', 'noopener');
    } else if (action === 'help') {
      window.open('/#/coupon-validation', '_blank');
    } else {
      this._router.navigateByUrl(action);
    }
  }

  /** Pause auto-rotation while the pointer is over the bar. */
  public pauseHints(): void {
    this.stopHintTimer();
  }

  /** Resume auto-rotation when the pointer leaves the bar. */
  public resumeHints(): void {
    this.startHintTimer();
  }

  private startHintTimer(): void {
    this.stopHintTimer();
    if (this.hintsHidden()) return;
    this.hintTimer = setInterval(
      () => this.hintIndex.update((i) => (i + 1) % this.hints.length),
      HINT_ROTATE_MS
    );
  }

  private stopHintTimer(): void {
    if (this.hintTimer) {
      clearInterval(this.hintTimer);
      this.hintTimer = undefined;
    }
  }

  private readHintsHidden(): boolean {
    try {
      return localStorage.getItem(HINTS_HIDDEN_KEY) === 'true';
    } catch {
      return false;
    }
  }

  /** Switch the dashboard period and re-fetch. */
  public setDays(days: number): void {
    if (this.days() === days) return;
    this.days.set(days);
    this.fetchDashboard();
  }

  /** Whether the chart has anything to plot. */
  public hasSeries(): boolean {
    return (this.dashboard()?.series?.length ?? 0) > 0;
  }

  /** Open the hidden file input for the verification proof. */
  public openProofPicker(input: HTMLInputElement): void {
    if (this.uploadingProof()) return;
    input.click();
  }

  /** Upload the selected verification proof document. */
  public onProofSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;

    this.uploadingProof.set(true);
    this._verificationApi.uploadProof(file).subscribe({
      next: () => {
        this.uploadingProof.set(false);
        this._notification.success(
          this._transloco.translate('stats.proofUploadSuccess')
        );
        this.fetchVerification();
      },
      error: () => {
        this.uploadingProof.set(false);
        this._notification.error(
          this._transloco.translate('stats.proofUploadError')
        );
      },
    });
  }

  /** Getting-started paragraph with remaining step names. */
  public startBannerText(): string {
    const remaining = this.remainingSteps();
    const names = remaining
      .map((step) =>
        this._transloco.translate(`stats.step.${step}`).toLowerCase()
      )
      .join(', ');

    return this._transloco.translate('stats.startText', {
      count: remaining.length,
      steps: this.getStepWord(remaining.length),
      names,
    });
  }

  /** Translated name of the immediate next onboarding step (what "Nastavi" does). */
  public nextStepLabel(): string {
    const next = this.remainingSteps()[0];
    return next ? this._transloco.translate(`stats.step.${next}`) : '';
  }

  /** Continue the onboarding at the first incomplete step. */
  public continueOnboarding(proofInput: HTMLInputElement): void {
    const next = this.remainingSteps()[0];
    switch (next) {
      case 'program':
        this._router.navigate(['/loyalty-programs']);
        break;
      case 'personalization': {
        const programId = this.firstProgramId();
        if (programId != null) {
          this._router.navigate(['/loyalty-programs/edit', programId], {
            queryParams: { section: 'personalization' },
          });
        } else {
          this._router.navigate(['/loyalty-programs']);
        }
        break;
      }
      case 'location':
        this._router.navigate(['/locations']);
        break;
      case 'coupon':
        this._router.navigate(['/coupons/add']);
        break;
      case 'verification':
        this.openProofPicker(proofInput);
        break;
      case 'fullVersion':
        this._router.navigate(['/join-lomy']);
        break;
      default:
        break;
    }
  }

  /** Croatian-locale integer formatting (e.g. 42.180). */
  public formatInt(value: number): string {
    return new Intl.NumberFormat('hr-HR', {
      maximumFractionDigits: 0,
    }).format(value ?? 0);
  }

  /**
   * Money KPI formatting (audit F-06): keep cents whenever the amount has them —
   * a €5,90 receipt must not read as "€6" and disagree with the Transakcije table.
   */
  public formatMoney(value: number): string {
    const amount = value ?? 0;
    const hasCents = Math.abs(amount % 1) > 1e-9;
    return new Intl.NumberFormat('hr-HR', {
      minimumFractionDigits: hasCents ? 2 : 0,
      maximumFractionDigits: hasCents ? 2 : 0,
    }).format(amount);
  }

  /** Build the delta badge view model; null hides the badge. */
  private buildDelta(
    deltaPct: number | null
  ): { up: boolean; text: string; neutral?: boolean } | null {
    if (deltaPct == null) return null;

    const text = new Intl.NumberFormat('hr-HR', {
      maximumFractionDigits: 1,
    }).format(Math.abs(deltaPct));

    // Exactly 0% is neither up nor down — render it neutral (no arrow, muted).
    if (deltaPct === 0) {
      return { up: false, text: `${text}%`, neutral: true };
    }

    return { up: deltaPct >= 0, text: `${text}%` };
  }

  /** Fetch dashboard KPIs, series and activity for the current period. */
  private fetchDashboard(): void {
    this.loading.set(true);
    // Capture the requested period so a stale response from a previous
    // selection (period switched while in flight) can be ignored.
    const requestedDays = this.days();

    this._dashboardApi.getDashboard(requestedDays).subscribe({
      next: (dashboard) => {
        if (this.days() !== requestedDays) return;
        this.dashboard.set(dashboard);
        this.dashboardError.set(false);
        this.loading.set(false);
        this.renderChart();
      },
      error: () => {
        if (this.days() !== requestedDays) return;
        this.dashboard.set(null);
        this.dashboardError.set(true);
        this.loading.set(false);
        this.destroyChart();
      },
    });
  }

  /** Fetch verification state; errors are silent (banner stays hidden). */
  private fetchVerification(): void {
    this._verificationApi.getVerification().subscribe({
      next: (verification) => {
        this.verification.set(verification);
        this.verificationLoaded.set(true);
      },
      error: () => {
        this.verification.set(null);
        this.verificationLoaded.set(true);
      },
    });
  }

  /** Resolve getting-started step completion from existing endpoints. */
  private fetchOnboardingSteps(): void {
    forkJoin({
      // Keep the full program list: it drives both the "program" step and the
      // "personalization" step (whether the appearance was customized), and
      // provides the program id for the continue-to-personalization route.
      programs: this._loyaltyPrograms.getCompanyLoyaltyPrograms().pipe(
        catchError(() => of([]))
      ),
      locations: this._locations.getCompanyLocations().pipe(
        map((locations) => locations.length > 0),
        catchError(() => of(false))
      ),
      companyStats: this._company.getCompanyStats().pipe(
        catchError(() => of(null))
      ),
      // On error treat the account as full version so the getting-started
      // banner never nags about an upgrade we could not confirm is pending.
      infoBox: this._company.checkForInfoBox().pipe(
        catchError(() => of(null))
      ),
    }).subscribe(({ programs, locations, companyStats, infoBox }) => {
      this.hasProgram.set(programs.length > 0);
      this.firstProgramId.set(programs[0]?.id ?? null);
      // Personalization is "done" once at least one program's appearance was
      // explicitly customized (setup seeds a default, non-customized row).
      this.hasPersonalization.set(
        programs.some((program) => program?.personalization?.customized === true)
      );
      this.hasLocation.set(locations);
      this.hasCoupon.set((companyStats?.activeCouponCount ?? 0) > 0);
      // Null (fetch failed) => assume full version so we never nag about an
      // upgrade we could not confirm; onWaitingList stays false in that case.
      this.hasFullVersion.set(infoBox ? infoBox.fullVersion === true : true);
      this.onWaitingList.set(infoBox?.onWaitingList === true);
      this.stepsLoaded.set(true);
    });
  }

  /** Ordered list of steps that are still incomplete. */
  public remainingSteps(): OnboardingStep[] {
    const remaining: OnboardingStep[] = [];
    if (!this.hasProgram()) remaining.push('program');
    // Personalization is the first step surfaced after the setup wizard.
    if (!this.hasPersonalization()) remaining.push('personalization');
    if (!this.hasLocation()) remaining.push('location');
    if (!this.hasCoupon()) remaining.push('coupon');
    if (!this.verificationDone()) remaining.push('verification');
    // Upgrading to the full version is the final step; a pending request counts
    // as handled so it drops off (the card shows the waiting-for-approval state).
    if (!this.hasFullVersion() && !this.upgradePending()) {
      remaining.push('fullVersion');
    }
    return remaining;
  }

  /** Render the points-over-time area chart for the current series. */
  private renderChart(): void {
    if (!this.hasSeries()) {
      this.destroyChart();
      return;
    }

    // Wait a tick so *ngIf renders the canvas before drawing.
    setTimeout(() => {
      const canvas = this.chartCanvas?.nativeElement;
      const series = this.dashboard()?.series;
      if (!canvas || !series?.length) return;

      this.destroyChart();

      const ctx = canvas.getContext('2d');
      let fill: CanvasGradient | string = 'rgba(254, 62, 170, 0.12)';
      if (ctx) {
        const gradient = ctx.createLinearGradient(0, 0, 0, 240);
        gradient.addColorStop(0, 'rgba(254, 62, 170, 0.25)');
        gradient.addColorStop(1, 'rgba(254, 62, 170, 0)');
        fill = gradient;
      }

      this.chart = new Chart(canvas, {
        type: 'line',
        data: {
          labels: series.map((point) => this.formatDateLabel(point.date)),
          datasets: [
            {
              label: this._transloco.translate('stats.pointsEarned'),
              data: series.map((point) => point.pointsEarned),
              borderColor: ACCENT,
              backgroundColor: fill,
              fill: true,
              tension: 0.35,
              borderWidth: 2.5,
              pointRadius: 0,
              pointHoverRadius: 5,
              pointHoverBackgroundColor: '#ffffff',
              pointHoverBorderColor: ACCENT,
              pointHoverBorderWidth: 2.5,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: { mode: 'index', intersect: false },
          plugins: { legend: { display: false } },
          scales: {
            x: {
              grid: { display: false },
              ticks: {
                font: { size: 10 },
                maxTicksLimit: 6,
                maxRotation: 0,
                minRotation: 0,
              },
            },
            y: {
              beginAtZero: true,
              grid: { color: '#E6E8EC' },
              ticks: { font: { size: 10 } },
            },
          },
        },
      });
    }, 0);
  }

  private destroyChart(): void {
    this.chart?.destroy();
    this.chart = undefined;
  }

  /** Chart x-axis label: dd.MM, or "danas" for today's point. */
  private formatDateLabel(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();

    if (
      date.getFullYear() === now.getFullYear() &&
      date.getMonth() === now.getMonth() &&
      date.getDate() === now.getDate()
    ) {
      return this._transloco.translate('stats.today');
    }

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${day}.${month}`;
  }

  /** Feed subtitle for a pending transaction: "€18,40 · 92 boda". */
  private buildPendingSub(amount: number | null, points: number | null): string {
    const parts: string[] = [];

    if (amount != null) {
      const formatted = new Intl.NumberFormat('hr-HR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
      parts.push(`€${formatted}`);
    }

    if (points != null) {
      parts.push(this.formatPoints(points));
    }

    return parts.join(' · ');
  }

  /** Points with the correct Croatian plural unit, e.g. "240 bodova". */
  private formatPoints(points: number): string {
    return `${this.formatInt(points)} ${this.getPointsWord(points)}`;
  }

  /** Croatian plural unit for points (bod / boda / bodova). */
  private getPointsWord(points: number): string {
    const mod100 = Math.abs(points) % 100;
    const mod10 = Math.abs(points) % 10;

    let key = 'stats.pointsUnitMany';
    if (mod100 < 11 || mod100 > 14) {
      if (mod10 === 1) key = 'stats.pointsUnitOne';
      else if (mod10 >= 2 && mod10 <= 4) key = 'stats.pointsUnitFew';
    }

    return this._transloco.translate(key);
  }

  /** Croatian plural for "korak" (steps remaining). */
  private getStepWord(count: number): string {
    const mod100 = Math.abs(count) % 100;
    const mod10 = Math.abs(count) % 10;

    let key = 'stats.stepUnitMany';
    if (mod100 < 11 || mod100 > 14) {
      if (mod10 === 1) key = 'stats.stepUnitOne';
      else if (mod10 >= 2 && mod10 <= 4) key = 'stats.stepUnitFew';
    }

    return this._transloco.translate(key);
  }

  /** Time-of-day greeting: jutro (5-12h) / dan (12-18h) / večer. */
  private resolveGreetingKey(): string {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 12) return 'stats.greetingMorning';
    if (hour >= 12 && hour < 18) return 'stats.greetingDay';
    return 'stats.greetingEvening';
  }

  /**
   * Format a timestamp to display how long ago it occurred, in the active language
   * (audit F-11 — this used to be hardcoded Croatian even on the English console).
   * Intl.RelativeTimeFormat covers both languages' plural rules (minutu/minute/minuta).
   */
  public formatTimeAgo(dateString: string): string {
    if (!dateString) return '';

    const transactionDate = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - transactionDate.getTime();

    const lang = this._transloco.getActiveLang() === 'en' ? 'en' : 'hr';
    const rtf = new Intl.RelativeTimeFormat(lang, { numeric: 'auto' });

    // Clamp future / sub-minute timestamps to "just now" ("now" / "sada").
    if (diffMs < 60000) return rtf.format(0, 'second');

    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return rtf.format(-diffMins, 'minute');
    if (diffHours < 24) return rtf.format(-diffHours, 'hour');
    return rtf.format(-diffDays, 'day');
  }
}
